import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { closeCustomModalDialog, setModalLoader } from '../../../redux/slices/modalSlice';
import { getDistributionSubregions } from '../../../redux/slices/hyperwolf/inventoryDistribution';
import { showToastMsg } from 'components/distributionSetting/distributionManagement/addRegionKit';
import MDButton from 'components/MDButton';

export default function SelectRegionModal({ closeModal }) {
  const dispatch = useDispatch();
  const { isLoading, modalData = {} } = useSelector(state => state.modal);
  const { onSuccess, onSave, templateType = 'distribution' } = modalData;

  const [subregions, setSubregions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [fetching, setFetching] = useState(false);

  const allSubregionIds = subregions.map((sub) => sub.subRegionId);
  const isAllSelected = subregions.length > 0 && selectedIds.length === subregions.length;

  const fetchSubregions = async () => {
    setFetching(true);
    try {
      const res = await dispatch(getDistributionSubregions({ templateType })).unwrap();
      setSubregions(res?.data || []);
    } catch (error) {
      showToastMsg(error?.message || 'Failed to load subregions', 'error');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchSubregions();
  }, []);
  const handleSave = async () => {
    if (isLoading || !selectedIds.length) return;
    try {
      dispatch(setModalLoader(true));
      const res = await dispatch(onSave({ subRegionIds: selectedIds })).unwrap();
      showToastMsg(res?.message || 'Success', 'success');
      if (typeof onSuccess === 'function') onSuccess();
      dispatch(closeCustomModalDialog());
    } catch (error) {
      showToastMsg(error?.message || error, 'error');
    } finally {
      dispatch(setModalLoader(false));
    }
  };

  return (
    <>
      <DialogTitle className="p-4">
        <Typography variant="h4" fontWeight="bold">
          Select Sub Regions
        </Typography>
        <IconButton
          onClick={() => closeModal(false)}
          edge="end"
          size="small"
          style={{ position: 'absolute', right: '24px', top: '24px' }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="px-4 py-0">
        <Typography variant="body2" sx={{ mb: 2, color: '#656575', fontSize: '14px' }}>
          Select one or more sub regions to distribute.
        </Typography>

        {fetching ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box className="cus-multi-select full-multi-select">
            <Select
              fullWidth
              multiple
              disabled={isLoading}
              value={selectedIds}
              onChange={(e) => {
                const { target: { value } } = e;
                if (value.includes('select-all')) {
                  setSelectedIds(isAllSelected ? [] : allSubregionIds);
                  return;
                }
                setSelectedIds(typeof value === 'string' ? value.split(',') : value);
              }}
              className="form-select"
              displayEmpty
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <Typography color="text.secondary" fontSize={14}>Select sub regions</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const sub = subregions.find((s) => s.subRegionId === id);
                      return (
                        <Chip
                          key={id}
                          label={sub?.subRegionName || sub?.name || id}
                          onDelete={(e) => {
                            e.preventDefault();
                            setSelectedIds((prev) => prev.filter((v) => v !== id));
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          sx={{ m: 0.25 }}
                        />
                      );
                    })}
                  </Box>
                )
              }
              MenuProps={{ classes: { root: 'cus-dropdown-height' } }}
            >
              {subregions.length === 0 ? (
                <MenuItem disabled>No sub regions available</MenuItem>
              ) : (
                [
                  <MenuItem key="select-all" value="select-all">
                    {isAllSelected ? 'Deselect All' : 'Select All'}
                  </MenuItem>,
                  ...subregions.map((sub) => (
                    <MenuItem key={sub.subRegionId} value={sub.subRegionId}>
                      {sub.subRegionName || sub.name}
                    </MenuItem>
                  )),
                ]
              )}
            </Select>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'start' }} className="p-4">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} mt={1}>
          <MDButton
            sx={{ minWidth: '150px' }}
            variant="contained"
            color="primary"
            disabled={isLoading || !selectedIds.length}
            onClick={handleSave}
          >
            {isLoading ? <CircularProgress size={22} color="white" /> : 'Save'}
          </MDButton>
          <MDButton
            sx={{ minWidth: '150px' }}
            variant="outlined"
            color="dark"
            onClick={() => closeModal(false)}
          >
            Cancel
          </MDButton>
        </Stack>
      </DialogActions>
    </>
  );
}
